<template>
  <b-container fluid>
    <admin-preloader :show="loading"/>
    <admin-breadcrumb :items="[{ text: 'Reports', path: {name: 'admin-reports'} }]" name="Sales by Date"/>
    <admin-panel with-header>
      <b-table
        :fields="reportColumns"
        :items="reportRows"
        table-class="text-nowrap"
        responsive
        hover
      />
      <template v-slot:header>
        <div class="d-flex justify-content-between">
          <div class="d-flex">
            <div class="mr-3">
              <strong class="text-uppercase">Start Date:</strong> {{ startDate }}
              <b-form-datepicker
                v-model="filters.start_date"
                class="ml-2"
                :max="maxDate"
                button-only
                button-variant="primary"
                @input="onStartDateChanged"
              />
            </div>
            <div>
              <strong class="text-uppercase">End Date:</strong> {{ endDate }}
              <b-form-datepicker
                v-model="filters.end_date"
                class="ml-2"
                :max="maxDate"
                button-only
                button-variant="primary"
                @input="onEndDateChanged"
              />
            </div>
          </div>
          <div>
            <b-btn
              variant="primary"
              v-b-tooltip.hover title="Download CSV"
              @click.prevent="downloadCsv"
            >
              <b-icon-download/>
            </b-btn>
            <b-btn
              variant="primary"
              v-b-tooltip.hover title="Reset Filters"
              @click.prevent="resetFilters"
            >
              <b-icon-arrow-counterclockwise/>
            </b-btn>
          </div>
        </div>
      </template>
      <template v-slot:header-right>
        <div class="mt-n2 d-flex flex-wrap">
          <b-form-select
            class="w-auto mr-2 my-2"
            v-model="filters.category_type"
            :options="categoryTypes"
            @change="() => loadReportData()"
          />
          <b-form-select
            class="w-auto mr-2 my-2"
            v-model="filters.category"
            :options="categories"
            @change="() => loadReportData()"
          />
          <b-form-select
            class="w-auto mr-2 my-2"
            v-model="filters.production_type"
            :options="productionTypes"
            @change="() => loadReportData()"
          />
          <b-form-select
            class="w-auto mr-2 my-2"
            v-model="filters.strain_type"
            :options="strainTypes"
            @change="() => loadReportData()"
          />
          <b-form-select
            class="w-auto my-2"
            v-model="filters.is_promoted"
            :options="promoted_filter_options"
            @change="() => loadReportData()"
          />
        </div>
      </template>
    </admin-panel>
  </b-container>
</template>

<script>
import {API_ROUTES} from "../api";
import AdminPreloader from "../components/AdminPreloader";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import AdminPanel from "../components/AdminPanel";
import moment from "moment";
import {getFormattedPrice} from "../helpers";

export default {
  name: "AdminReportSalesByDate",
  components: {AdminPanel, AdminBreadcrumb, AdminPreloader},
  data() {
    let query = this.$route.query;

    return {
      loading: true,
      report_data: null,
      filters: {
        start_date: query.start_date ?? moment().startOf('month').format('YYYY-MM-DD'),
        end_date: query.end_date ?? moment().format('YYYY-MM-DD'),
        category_type: query.category_type ?? "",
        is_promoted: query.is_promoted ?? "",
        category: query.category ?? "",
        strain_type: query.strain_type ?? "",
        production_type: query.production_type ?? ""
      },
      promoted_filter_options: [
        {
          value: "",
          text: "All Listings"
        },
        {
          value: "1",
          text: "Only Featured"
        },
        {
          value: "0",
          text: "Only Non-Featured"
        },
      ],
      reportColumns: [
        {
          key: "date",
          thClass: "border-top-0",
          tdClass: "align-middle"
        },
        {
          key: "sales",
          thClass: "border-top-0",
          tdClass: "align-middle"
        },
        {
          key: "win_price",
          thClass: "border-top-0",
          tdClass: "align-middle",
          formatter: value => getFormattedPrice(value)
        },
        {
          key: "shipping_price",
          label: 'Logistic Fee',
          thClass: "border-top-0",
          tdClass: "align-middle",
          formatter: value => getFormattedPrice(value)
        },
        {
          key: "seller_fee",
          label: 'Seller Fee',
          thClass: "border-top-0",
          tdClass: "align-middle",
          formatter: value => getFormattedPrice(value)
        },
        {
          key: "buyer_fee",
          label: 'Buyer Fee',
          thClass: "border-top-0",
          tdClass: "align-middle",
          formatter: value => getFormattedPrice(value)
        },
        {
          key: "total_fee",
          label: 'Total Fee',
          thClass: "border-top-0",
          tdClass: "align-middle",
          formatter: value => getFormattedPrice(value)
        },
        {
          key: "seller_fee_paid",
          label: 'Seller Fee Paid',
          thClass: "border-top-0",
          tdClass: "align-middle",
          formatter: value => getFormattedPrice(value)
        },
        {
          key: "buyer_fee_paid",
          label: 'Buyer Fee Paid',
          thClass: "border-top-0",
          tdClass: "align-middle",
          formatter: value => getFormattedPrice(value)
        },
        {
          key: "total_fee_paid",
          label: 'Total Fee Paid',
          thClass: "border-top-0",
          tdClass: "align-middle",
          formatter: value => getFormattedPrice(value)
        }
      ]
    }
  },
  methods: {
    setQueryVar(key, value) {
      let query = { ...this.$route.query };
      if (!value && typeof query[key] !== 'undefined') {
        delete query[key];
      } else if (value) {
        query[key] = value;
      }
      this.$router.push({query});
    },
    onStartDateChanged(startDate) {
      if (moment(startDate).isAfter(moment(this.filters.end_date))) {
        this.filters.end_date = startDate;
      }
      this.loadReportData();
    },
    onEndDateChanged(endDate) {
      if (moment(endDate).isBefore(moment(this.filters.start_date))) {
        this.filters.start_date = endDate;
      }
      this.loadReportData();
    },
    loadReportData() {
      let params = {
        start_date: this.filters.start_date,
        end_date: this.filters.end_date
      }
      if (this.filters.category_type) {
        params['category_type'] = this.filters.category_type;
      }
      if (this.filters.category) {
        params['category'] = this.filters.category;
      }
      if (this.filters.production_type) {
        params['production_type'] = this.filters.production_type;
      }
      if (this.filters.strain_type) {
        params['strain_type'] = this.filters.strain_type;
      }
      if (this.filters.is_promoted !== '') {
        params['is_promoted'] = this.filters.is_promoted;
      }
      this.loading = true;
      this.$api.get(`${API_ROUTES.ADMIN_REPORT_SALES_BY_DATE}`, {params}).then(({data}) => {
        this.report_data = data.report_data;
      }).catch(({response}) => {
        let message = 'Failed to load report';
        if (response.status === 422) {
          message = 'Some of your params were incorrect';
          this.resetFilters();
        }
        this.$bvToast.toast(message, {
          title: 'Error!',
          variant: 'danger'
        });
      }).finally(() => {
        this.loading = false;
      })
    },
    downloadCsv() {
      if (this.reportRows.length > 2) {
        let csvContent = "data:text/csv;charset=utf-8,";
        let headers = [
          'Date',
          'Sales',
          'Win Price',
          'Logistic Fee',
          'Seller Fee',
          'Buyer Fee',
          'Total Fee',
          'Seller Fee Paid',
          'Buyer Fee Paid',
          'Total Fee Paid'
        ];
        csvContent += headers.join(",") + "\r\n";
        for (let i = 1; i < this.reportRows.length - 1; i++) { //remove total columns.
          let row = this.reportRows[i];
          let rowData = [
            row.date,
            row.sales,
            row.win_price,
            row.shipping_price,
            row.seller_fee,
            row.buyer_fee,
            row.total_fee,
            row.seller_fee_paid,
            row.buyer_fee_paid,
            row.total_fee_paid
          ];
          csvContent += rowData.join(",") + "\r\n";
        }
        let encodedUri = encodeURI(csvContent),
          link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "gl_sales_by_date.csv");
        document.body.appendChild(link);
        link.click();
      }
    },
    resetFilters() {
      this.filters = {
        start_date: moment().startOf('month').format('YYYY-MM-DD'),
        end_date: moment().format('YYYY-MM-DD'),
        category_type: "",
        is_promoted: "",
        category: "",
        strain_type: "",
        production_type: ""
      };
      this.loadReportData();
    }
  },
  computed: {
    maxDate() {
      return moment().format('YYYY-MM-DD');
    },
    startDate() {
      return moment(this.filters.start_date).format('L');
    },
    endDate() {
      return moment(this.filters.end_date).format('L');
    },
    categoryTypes() {
      let categoryTypes = this.$store.state.data.category_types
        .map(categoryType => ({
          value: categoryType.id,
          text: categoryType.name
        }));
      categoryTypes.unshift({
        value: '',
        text: 'All Category Types'
      });
      return categoryTypes;
    },
    categories() {
      let categories = this.$store.state.data.categories
        .filter(category => category.name !== 'Other')
        .map(category => ({
          value: category.id,
          text: category.name,
          category_type_id: category.category_type_id
        }));
      if (this.filters.category_type) {
        categories = categories.filter(category => category.category_type_id === this.filters.category_type);
      }
      categories.unshift({
        value: '',
        text: 'All Categories'
      });
      return categories;
    },
    productionTypes() {
      let productionTypes = this.$store.state.data.production_types.map(productionType => ({
        value: productionType.id,
        text: productionType.name,
        category_types: productionType.category_types
      }));
      if (this.filters.category_type) {
        productionTypes = productionTypes.filter(productionType => productionType.category_types.indexOf(this.filters.category_type) >= 0);
      }
      productionTypes.unshift({
        value: '',
        text: 'All Productions'
      });
      return productionTypes;
    },
    strainTypes() {
      let strainTypes = this.$store.state.data.strain_types.map(strainType => ({
        value: strainType.id,
        text: strainType.name
      }));
      strainTypes.unshift({
        value: '',
        text: 'All Strain Types'
      });
      return strainTypes;
    },
    reportRows() {
      if (!this.report_data) {
        return [];
      }

      let totalRow = {
        date: 'TOTAL',
        sales: this.report_data.total.sales,
        win_price: this.report_data.total.win_price,
        shipping_price: this.report_data.total.shipping_price,
        seller_fee: this.report_data.total.seller_fee,
        buyer_fee: this.report_data.total.buyer_fee,
        total_fee: this.report_data.total.total_fee,
        seller_fee_paid: this.report_data.total.seller_fee_paid,
        buyer_fee_paid: this.report_data.total.buyer_fee_paid,
        total_fee_paid: this.report_data.total.total_fee_paid,
        _rowVariant: 'success'
      };
      let rows = [totalRow];

      for (let date in this.report_data.dates) {
        rows.push({
          date: moment(date).format('L'),
          sales: this.report_data.dates[date].sales,
          win_price: this.report_data.dates[date].win_price,
          shipping_price: this.report_data.dates[date].shipping_price,
          seller_fee: this.report_data.dates[date].seller_fee,
          buyer_fee: this.report_data.dates[date].buyer_fee,
          total_fee: this.report_data.dates[date].total_fee,
          seller_fee_paid: this.report_data.dates[date].seller_fee_paid,
          buyer_fee_paid: this.report_data.dates[date].buyer_fee_paid,
          total_fee_paid: this.report_data.dates[date].total_fee_paid
        })
      }

      rows.push(totalRow);

      return rows;
    }
  },
  watch: {
    "filters.category_type": function (categoryTypeId) {
      if (categoryTypeId) {
        if (this.filters.category) {
          let category = this.$store.state.data.categories.find(
            category => category.id === this.filters.category && category.category_type_id === categoryTypeId
          );
          if (!category) {
            this.filters.category = '';
          }
        }
        if (this.filters.production_type) {
          let productionType = this.$store.state.data.production_types.find(
            productionType => productionType.id === this.filters.production_types && productionType.category_types.indexOf(categoryTypeId) >= 0
          );
          if (!productionType) {
            this.filters.production_type = '';
          }
        }
      }
      this.setQueryVar('category_type', categoryTypeId);
    },
    "filters.category": function (categoryId) {
      this.setQueryVar('category', categoryId);
    },
    "filters.production_type": function (productionTypeId) {
      this.setQueryVar('production_type', productionTypeId);
    },
    "filters.strain_type": function (strainTypeId) {
      this.setQueryVar('strain_type', strainTypeId);
    },
    "filters.is_promoted": function (isPromoted) {
      this.setQueryVar('is_promoted', isPromoted);
    },
    "filters.start_date": function (startDate) {
      if (startDate === moment().startOf('month').format('YYYY-MM-DD')) {
        startDate = null;
      }
      this.setQueryVar('start_date', startDate);
    },
    "filters.end_date": function (endDate) {
      if (endDate === moment().format('YYYY-MM-DD')) {
        endDate = null;
      }
      this.setQueryVar('end_date', endDate);
    },
  },
  created() {
    this.loadReportData();
  }
}
</script>